import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  Typography,
  createFilterOptions
} from '@mui/material'
import { forwardRef, Fragment, useEffect, useState } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
import ClearIcon from '@mui/icons-material/Clear'
import AddIcon from '@mui/icons-material/Add'

// others
import {
  ExternalUser,
  ExternalUserCaseRole,
  ExternalUserRoleRole,
  useFetchAllExternalUsersLazyQuery
} from '../../../../../generated'
import { EXTERNAL_USER_ADVERSARY_CASE_ROLES, GRAPHQL_QUERY_POLICY } from '../../../../constants'
import { titleize } from '../../../../utils'
import { menuHeight } from '../../../../theme/styleConstants'
import { ExternalUserInterface } from '../../../../interfaceTypes'
import { AddNewAdversary } from './AddNewAdversary'
import { ExternalUsersResult } from '../../../../interfaceTypes/common'

type AdversaryUserListRef = {
  appendAdversaryUser: (user: ExternalUser) => void
}

export const AdversaryUserList = forwardRef<AdversaryUserListRef, {}>((props, ref) => {
  const [externalUsersData, setExternalUsersData] = useState<ExternalUsersResult>([])
  const [newExternalUser, setNewExternalUser] = useState<ExternalUser>()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)

  const [openModal, setOpenModal] = useState(false)

  const handleOpen = () => setOpenModal(true)
  const handleClose = () => setOpenModal(false)

  const {
    control,
    formState: { errors }
  } = useFormContext<{
    adversaryUsers: ExternalUserInterface[]
  }>()
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'adversaryUsers'
  })

  const [fetchAllExternalUsers, { loading }] = useFetchAllExternalUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onCompleted(data) {
      if (data) {
        const { fetchAllExternalUsers } = data

        if (fetchAllExternalUsers) {
          const { externalUsers, response, pagination } = fetchAllExternalUsers
          const { page } = pagination || {}
          const { status } = response || {}

          if (status === 200 && externalUsers) {
            externalUsersData && page !== 1
              ? setExternalUsersData([...externalUsersData, ...externalUsers])
              : setExternalUsersData(externalUsers)
          }
        }
      }
    }
  })

  const getOptions = (): ExternalUserInterface[] =>
    externalUsersData?.map(user => ({
      id: user?.id || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      displayName: user?.displayName ?? '',
      email: user?.email || ''
    })) ?? []

  useEffect(() => {
    fetchAllExternalUsers({
      variables: {
        externalUsersInput: {
          roles: [ExternalUserRoleRole.Adversary],

          paginationOptions: {
            limit: 20,
            page: currentPage
          },
          searchValue: debouncedSearchTerm
        }
      }
    })
    // eslint-disable-next-line
  }, [currentPage, debouncedSearchTerm])

  useEffect(() => {
    if (debouncedSearchTerm) {
      setCurrentPage(1)
    }
  }, [debouncedSearchTerm])

  useEffect(() => {
    if (newExternalUser?.id) {
      const focusedFieldIndex = fields.length ? fields.length - 1 : 0

      update(focusedFieldIndex, {
        id: newExternalUser.id,
        email: newExternalUser.email || '',
        firstName: newExternalUser.firstName,
        lastName: newExternalUser.lastName,
        caseRole: null
      })
    }
  }, [newExternalUser])

  const adversaryRow = (index: number) => {
    const name = `adversaryUsers.${index}` as const

    return (
      <Grid container item spacing={2.5} alignItems="center" sx={{ position: 'relative' }}>
        <Grid item xs={6} md={8}>
          <Controller
            name={name}
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                fullWidth
                options={getOptions() || []}
                value={value}
                filterOptions={createFilterOptions({ ignoreCase: true })}
                loading={loading}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                onChange={(event, item) => {
                  onChange(item ? { ...value, ...item } : null)
                }}
                onInputChange={(event, newInputValue) => {
                  setSearchTerm(newInputValue)
                }}
                ListboxProps={{
                  onScroll: event => {
                    const listboxNode = event.currentTarget
                    if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
                      setCurrentPage(currentPage + 1)
                    }
                  },
                  role: 'list-box',
                  style: {
                    maxHeight: '190px'
                  }
                }}
                getOptionLabel={item => {
                  const { firstName, lastName, displayName, email, id } = (item || {}) as {
                    firstName?: string
                    lastName?: string
                    displayName?: string | null
                    email?: string
                    id?: string
                  }
                  const name = displayName?.trim() || `${firstName || ''} ${lastName || ''}`.trim()
                  const options = `${name} ${email ? `(${email})` : ''}`
                  return id ? options : ''
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    error={!!errors.adversaryUsers?.[index]?.id}
                    label="Adversary Contacts"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={6} md={4}>
          <FormControl variant="outlined" fullWidth>
            <Controller
              name={`${name}.caseRole`}
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value }, fieldState: { error: { message } = {} } }) => {
                return (
                  <Autocomplete
                    fullWidth
                    disableClearable
                    options={
                      EXTERNAL_USER_ADVERSARY_CASE_ROLES.filter(
                        adversaryRole => adversaryRole.value !== ExternalUserCaseRole.PrimaryAdversary
                      ) || []
                    }
                    onChange={(event, item) => {
                      onChange(item.value)
                    }}
                    ListboxProps={menuHeight}
                    value={EXTERNAL_USER_ADVERSARY_CASE_ROLES.find(item => item.value === value)}
                    getOptionLabel={value => value?.label || ''}
                    filterOptions={createFilterOptions({ ignoreCase: true })}
                    isOptionEqualToValue={(option, value) => {
                      return option?.value === value?.value
                    }}
                    renderInput={params => (
                      <TextField {...params} error={!!errors.adversaryUsers?.[index]?.caseRole} label="Matter Role" />
                    )}
                  />
                )
              }}
            />
          </FormControl>
        </Grid>

        {index > 0 && (
          <IconButton onClick={() => remove(index)} style={{ position: 'absolute', right: -42 }}>
            <ClearIcon color="primary" />
          </IconButton>
        )}
        <Grid md={8} />

        <Grid
          item
          md={4}
          xs={12}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{
            paddingTop: '4px !important'
          }}
        >
          <FormHelperText error={!!errors.adversaryUsers?.[index]?.caseRole}>
            {typeof errors.adversaryUsers?.[index]?.caseRole?.message === 'string'
              ? errors.adversaryUsers?.[index]?.caseRole?.message
              : ' '}
          </FormHelperText>
        </Grid>

        <AddNewAdversary
          customOpen={openModal}
          handleCustomClose={handleClose}
          setNewExternalUser={setNewExternalUser}
        />
      </Grid>
    )
  }

  return (
    <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
      <Typography variant="h3" fontSize={14} fontWeight={700} color={'primary.main'}>
        Adversary Contacts
      </Typography>
      <FormControl variant="outlined" fullWidth>
        <Grid container>
          {fields.map((field, index) => (
            <Fragment key={field.id}>{adversaryRow(index)}</Fragment>
          ))}

          <Grid
            item
            md={8}
            xs={12}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}
            sx={{ pt: '0 !important', position: 'relative', bottom: 28 }}
          >
            <Button
              onClick={() =>
                append({
                  id: '',
                  firstName: '',
                  lastName: '',
                  email: '',
                  caseRole: null
                })
              }
              startIcon={<AddIcon />}
              variant="text"
              sx={{ fontSize: 11, pl: 1 }}
            >
              Add Adversary Contact
            </Button>

            <Box display="flex" alignItems="center">
              <Button size="small" variant="text" onClick={handleOpen} disabled={!fields.length} sx={{ marginLeft: 1 }}>
                <span style={{ fontSize: 11 }}>{titleize(`Create New`)}</span>
              </Button>
            </Box>
          </Grid>
        </Grid>
      </FormControl>
    </Box>
  )
})
